import React, { useEffect, useMemo, useState } from "react"
import axios from "axios"
import PropTypes from "prop-types"
import CircularProgress from "@mui/material/CircularProgress"
import { environmentData } from "../../environments/env"
import "./SharePointCalendar.scss"
import "./SharePointActivity.scss"

const MAX_ACTIVITIES = 6
const CET_TIME_ZONE = "Europe/Berlin"

const pad = (value) => String(value).padStart(2, "0")

// UTC offset (ms) of the CET/CEST zone at the given instant.
const getCetOffsetMs = (instant) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: CET_TIME_ZONE,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant)
  const get = (type) => Number(parts.find((p) => p.type === type).value)
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
  )
  return asUtc - instant.getTime()
}

// End of the given YYYY-MM-DD (23:59:59 CET/CEST) as a UTC timestamp.
export const getCetEndOfDay = (dateStr) => {
  const [year, month, day] = String(dateStr).split("-").map(Number)
  if (!year || !month || !day) return null
  const wallClockAsUtc = Date.UTC(year, month - 1, day, 23, 59, 59)
  return wallClockAsUtc - getCetOffsetMs(new Date(wallClockAsUtc))
}

export const getTimeLeft = (targetMs, nowMs) => {
  const diff = Math.max(0, targetMs - nowMs)
  const totalSeconds = Math.floor(diff / 1000)
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  }
}

const ActivityCountdown = ({ activity, now }) => {
  const targetMs = getCetEndOfDay(activity.endDate)
  if (targetMs == null) return null

  const { days, hours, minutes, seconds } = getTimeLeft(targetMs, now)
  const units = [
    { value: days, label: "days" },
    { value: hours, label: "hrs" },
    { value: minutes, label: "min" },
    { value: seconds, label: "sec" },
  ]

  return (
    <section className="sharepoint-activity" aria-label={activity.name}>
      <h2 className="sharepoint-activity-title">{activity.name}</h2>
      <div className="sharepoint-activity-countdown" role="timer">
        {units.map(({ value, label }) => (
          <div key={label} className="sharepoint-activity-unit">
            <span className="sharepoint-activity-value">{pad(value)}</span>
            <span className="sharepoint-activity-label">{label}</span>
          </div>
        ))}
      </div>
      {activity.category && <p className="sharepoint-activity-subtitle">{activity.category}</p>}
    </section>
  )
}

ActivityCountdown.propTypes = {
  activity: PropTypes.shape({
    name: PropTypes.string,
    category: PropTypes.string,
    endDate: PropTypes.string,
  }).isRequired,
  now: PropTypes.number.isRequired,
}

const SharePointActivity = () => {
  const isEmbedded = window.self !== window.top || process.env.NODE_ENV === "development"
  const [calendarData, setCalendarData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!isEmbedded) return undefined
    let cancelled = false
    const loadCalendar = async () => {
      try {
        const configuredShopApiUrl = environmentData[environmentData.run].shopUrl
        const shopApiUrl = configuredShopApiUrl.endsWith("/")
          ? configuredShopApiUrl.slice(0, -1)
          : configuredShopApiUrl
        const response = await axios.get(`${shopApiUrl}/get_cto_campaign_calendar_copy`)
        if (!cancelled) setCalendarData(response?.data ?? null)
      } catch (err) {
        console.error("Failed to fetch get_cto_campaign_calendar_copy", err)
        if (!cancelled) setError("Unable to load activity.")
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    void loadCalendar()
    return () => {
      cancelled = true
    }
  }, [isEmbedded])

  const activities = useMemo(() => {
    const items = Array.isArray(calendarData?.items) ? calendarData.items : []
    return items
      .filter((item) => item?.type === "activity" && item?.endDate)
      .slice(0, MAX_ACTIVITIES)
  }, [calendarData])

  const hasActivities = activities.length > 0

  useEffect(() => {
    if (!hasActivities) return undefined
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [hasActivities])

  if (!isEmbedded) {
    return (
      <main className="sharepoint-calendar-not-found">
        <section className="sharepoint-calendar-not-found-content" aria-labelledby="activity-not-found-title">
          <p className="sharepoint-calendar-not-found-code" aria-label="Error 404">404</p>
          <div className="sharepoint-calendar-not-found-copy">
            <p className="sharepoint-calendar-not-found-eyebrow">CAMPAIGN ACTIVITY</p>
            <h1 id="activity-not-found-title">Page not found</h1>
            <a href="/">Go to Intelligent Insights</a>
          </div>
        </section>
      </main>
    )
  }

  if (isLoading) {
    return (
      <div className="sharepoint-activity-loader" role="status" aria-label="Loading">
        <CircularProgress size={32} thickness={5} sx={{ color: "#111111" }} />
      </div>
    )
  }
  if (error) return <p role="alert">{error}</p>
  if (!hasActivities) return null

  return (
    <div className="sharepoint-activity-grid">
      {activities.map((activity) => (
        <ActivityCountdown key={activity.id ?? activity.name} activity={activity} now={now} />
      ))}
    </div>
  )
}

export default SharePointActivity
